import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import {
  createTask,
  appStoreLookup,
  ALL_TASK_CATEGORIES,
  ALL_TASK_TYPES,
  ALL_TASK_PLATFORMS,
  TASK_CATEGORY_LABELS,
  TASK_TYPE_LABELS,
  TASK_PLATFORM_LABELS,
  previewMessageImport,
  commitMessageImport,
} from '../features/tasks';
import type { AnyTaskType, TaskCategory, TaskPlatform, TaskStepDTO, DownloadAppInfoDTO, DownloadKeywordQuotaDTO } from '../features/tasks';
import { TaskStepEditor } from '../features/tasks/components/TaskStepEditor';
import { RichTaskTextEditor } from '../features/tasks/components/RichTaskTextEditor';
import { MessageImportPanel } from '../features/tasks/components/MessageImportPanel';
import {
  buildLookupSearchKeywords,
  buildSearchKeywordQuotasFromKeywords,
  normalizeSearchKeywordQuotas,
  sumSearchKeywordTargetCounts,
} from '../features/tasks/utils/searchKeywords';
import { useToastStore } from '../store/toast.store';
import { normalizeImageValueForSubmit, uploadImageToCos, resolveImagePreviewUrl } from '../lib/upload/cos';
import { useMarketConfigs } from '../features/settings';
import type { MarketConfigDTO } from '../features/settings/types/settings.types';

// --- Form state ---

interface FormState {
  title: string;
  description: string;
  description_title: string;
  category: string;
  task_type: string;
  market_id: string;
  platform: string;
  icon_url: string;
  tags: string;
  rules: string;
  rules_title: string;
  review_rules: string;
  review_rules_title: string;
  rewardYuan: string;
  max_assignments: string;
  completed_count: string;
  assignment_timeout_minutes: string;
  require_review: boolean;
  is_recommended: boolean;
  is_new: boolean;
  sort_order: string;
  start_at: string;
  end_at: string;
}

const INITIAL: FormState = {
  title: '',
  description: '',
  description_title: '',
  category: 'experience',
  task_type: 'normal',
  market_id: '',
  platform: 'all',
  icon_url: '',
  tags: '',
  rules: '',
  rules_title: '',
  review_rules: '',
  review_rules_title: '',
  rewardYuan: '',
  max_assignments: '0',
  completed_count: '0',
  assignment_timeout_minutes: '2',
  require_review: true,
  is_recommended: false,
  is_new: false,
  sort_order: '0',
  start_at: '',
  end_at: '',
};

function emptyStep(): TaskStepDTO {
  return { id: crypto.randomUUID(), order: 0, title: '', description: '', images: [], links: [] };
}

function emptyAppInfo(): DownloadAppInfoDTO {
  return {
    id: 0, bundle_id: '', search_keywords: [], search_keyword_quotas: [], name: '', version: '', price: 0, formatted_price: '',
    artwork_url: '', developer_name: '', description: '', genre: '',
    file_size_bytes: 0, rating: 0, rating_count: 0, content_rating: '', screenshot_urls: [],
  };
}

function emptyKeywordQuota(): DownloadKeywordQuotaDTO {
  return { keyword: '', target_count: 1, completed_count: 0 };
}

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      invalid_request_body: '请求参数不合法',
      network_error: '网络连接失败',
    };
    return map[e.message] ?? `创建失败 (${e.message})`;
  }
  return '创建失败';
}

function resolveLookupError(e: unknown): string {
  if (!isApiError(e)) return '查询失败';
  const map: Record<string, string> = {
    app_store_app_not_found: '公开 App Store 未找到该应用，请核对 App ID 或切换地区',
    invalid_app_store_lookup_input: 'App ID、Bundle ID 或地区格式不正确',
    network_error: '网络连接失败',
  };
  return map[e.message] ?? `查询失败 (${e.message})`;
}

function resolveMessageImportError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      invalid_message_import_file: '号码文件格式不正确',
      invalid_message_import_batch: '号码导入批次无效',
      message_import_batch_not_found: '号码导入批次不存在',
      task_not_found: '任务不存在',
      network_error: '网络连接失败',
    };
    return map[e.message] ?? e.message;
  }
  if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
    return e.message;
  }
  return '号码导入失败';
}

// --- Shared form field class ---

const inputCls = 'w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50';
const textareaCls = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y';
const selectCls = 'h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50';

function defaultMarketConfig(): MarketConfigDTO {
  return {
    market_id: 'CN',
    country_code: 'CN',
    name: '中国',
    locale: 'zh-CN',
    currency: 'CNY',
    currency_symbol: '¥',
    currency_exponent: 2,
    enabled: true,
    enabled_task_types: ['normal', 'other_normal', 'download', 'message_send'],
    enabled_features: {},
    requires_manual_payout: false,
    country_confirmation_required: false,
    task_configs: [],
  };
}

function parseRewardMinor(value: string, exponent: number): number {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (exponent <= 0) return Math.round(amount);
  return Math.round(amount * Math.pow(10, exponent));
}

export function TaskCreatePage() {
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);
  const { items: marketConfigs } = useMarketConfigs();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [steps, setSteps] = useState<TaskStepDTO[]>([]);
  const [appInfo, setAppInfo] = useState<DownloadAppInfoDTO>(emptyAppInfo());
  const [lookupCountry, setLookupCountry] = useState('cn');
  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stepUploads, setStepUploads] = useState<Record<string, boolean>>({});
  const [iconUploading, setIconUploading] = useState(false);
  const [uploadedIconUrl, setUploadedIconUrl] = useState('');
  const [messageImportFile, setMessageImportFile] = useState<File | null>(null);
  const [messageImportKey, setMessageImportKey] = useState<string>(() => crypto.randomUUID());

  const selectedMarket = useMemo(
    () => marketConfigs.find((item) => item.market_id === form.market_id) ?? marketConfigs[0] ?? defaultMarketConfig(),
    [form.market_id, marketConfigs],
  );
  const enabledTaskTypes = selectedMarket.enabled_task_types.length > 0
    ? selectedMarket.enabled_task_types
    : ALL_TASK_TYPES;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'market_id') {
        const nextMarket = marketConfigs.find((item) => item.market_id === value);
        const nextTaskTypes = nextMarket?.enabled_task_types?.length ? nextMarket.enabled_task_types : ALL_TASK_TYPES;
        if (!nextTaskTypes.includes(next.task_type)) {
          next.task_type = nextTaskTypes[0] ?? 'normal';
        }
      }
      if (key === 'task_type' && value === 'message_send') {
        next.platform = 'ios';
        next.require_review = true;
      }
      return next;
    });
  };

  const rewardMinor = parseRewardMinor(form.rewardYuan, selectedMarket.currency_exponent);
  const rewardFen = rewardMinor;
  const maxAssignments = parseInt(form.max_assignments, 10) || 0;
  const downloadMaxAssignments = sumSearchKeywordTargetCounts(
    normalizeSearchKeywordQuotas(appInfo.search_keyword_quotas ?? []),
  );
  const completedCount = parseInt(form.completed_count, 10) || 0;
  const assignmentTimeoutSeconds = Math.max(0, Math.round((parseFloat(form.assignment_timeout_minutes || '0') || 0) * 60));
  const sortOrder = parseInt(form.sort_order || '0', 10);
  const isMessageSend = form.task_type === 'message_send';
  const responsiveMessageGridClass = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';
  const basicInfoGridClass = isMessageSend ? responsiveMessageGridClass : 'grid grid-cols-4 gap-4';
  const rewardGridClass = isMessageSend ? responsiveMessageGridClass : 'grid grid-cols-3 gap-4';
  const timeAndSortGridClass = isMessageSend ? responsiveMessageGridClass : 'grid grid-cols-2 gap-4';

  const canSubmit =
    form.title.trim() !== '' &&
    form.market_id.trim() !== '' &&
    form.category !== '' &&
    rewardMinor > 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || Object.values(stepUploads).some(Boolean)) { if (Object.values(stepUploads).some(Boolean)) addToast("error", "请等待视频上传和处理完成后再保存"); return; }
    if (form.task_type === 'normal' && appInfo.search_keywords?.[0]?.trim() && (!Number.isSafeInteger(appInfo.id) || appInfo.id <= 0)) {
      addToast('error', '配置关键词时，请填写有效的 App Store 应用 ID');
      return;
    }
    setSubmitting(true);
    try {
      const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
      const searchKeywordQuotas = normalizeSearchKeywordQuotas(appInfo.search_keyword_quotas ?? []);
      const effectiveMaxAssignments = form.task_type === 'download'
        ? sumSearchKeywordTargetCounts(searchKeywordQuotas)
        : isMessageSend
          ? 0
        : maxAssignments;
      const createdTask = await createTask({
        title: form.title.trim(),
        description: form.description.trim(),
        description_title: form.description_title.trim(),
        category: form.category,
        task_type: form.task_type,
        platform: isMessageSend ? 'ios' : form.platform,
        market_id: selectedMarket.market_id,
        country_code: selectedMarket.country_code,
        currency: selectedMarket.currency,
        currency_symbol: selectedMarket.currency_symbol,
        currency_exponent: selectedMarket.currency_exponent,
        reward_amount_minor: rewardMinor,
        icon_url: normalizeImageValueForSubmit(form.icon_url.trim()),
        tags,
        rules: form.rules,
        rules_title: form.rules_title.trim(),
        review_rules: form.review_rules,
        review_rules_title: form.review_rules_title.trim(),
        reward_amount_fen: rewardFen,
        completed_count: completedCount,
        max_assignments: effectiveMaxAssignments,
        assignment_timeout_seconds: assignmentTimeoutSeconds,
        require_review: isMessageSend ? true : form.require_review,
        is_recommended: form.is_recommended,
        is_new: form.is_new,
        steps: steps.map((s, i) => ({
          ...s,
          order: i + 1,
          images: (s.images ?? []).map((image) => normalizeImageValueForSubmit(image)),
        })),
        app_info: form.task_type === 'download'
          ? {
              ...appInfo,
              search_keyword_quotas: searchKeywordQuotas,
              search_keywords: searchKeywordQuotas.map((item) => item.keyword),
            }
          : form.task_type === 'normal' && appInfo.search_keywords?.[0]?.trim()
            ? { ...emptyAppInfo(), id: appInfo.id, search_keywords: [appInfo.search_keywords[0].trim()] }
            : null,
        sort_order: sortOrder,
        ...(form.start_at ? { start_at: new Date(form.start_at).toISOString() } : {}),
        ...(form.end_at ? { end_at: new Date(form.end_at).toISOString() } : {}),
      });

      if (isMessageSend && messageImportFile) {
        try {
          const importBatch = await previewMessageImport(createdTask.id, messageImportFile, messageImportKey.trim());
          let messageImportBatch = importBatch;
          if (importBatch.valid_rows > 0) {
            const committedBatch = await commitMessageImport(createdTask.id, String(importBatch.batch_id));
            messageImportBatch = { ...committedBatch, rows: importBatch.rows ?? [] };
            const issueSuffix = importBatch.invalid_rows > 0
              ? `，${importBatch.invalid_rows} 行需修正后再导入`
              : '';
            addToast('success', `任务创建成功，已导入 ${importBatch.valid_rows} 条合规号码${issueSuffix}`);
          } else {
            addToast('error', '任务已创建，但号码文件没有合规数据，请在详情页修正后重新导入');
          }
          navigate(`/tasks/${createdTask.id}`, { state: { messageImportBatch } });
          return;
        } catch (importError) {
          addToast('error', `任务已创建，号码导入失败：${resolveMessageImportError(importError)}`);
          navigate(`/tasks/${createdTask.id}`);
          return;
        }
      } else {
        addToast('success', '任务创建成功');
        navigate('/tasks');
      }
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Steps helpers ---

  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (idx: number) => setSteps((prev) => prev.filter((_, i) => i !== idx));
  const updateStep = (idx: number, patch: Partial<TaskStepDTO>) =>
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const moveStep = (idx: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  // --- AppInfo helpers ---

  const setAppField = <K extends keyof DownloadAppInfoDTO>(key: K, value: DownloadAppInfoDTO[K]) => {
    setAppInfo((prev) => ({ ...prev, [key]: value }));
  };

  const addKeywordQuota = () => {
    setAppInfo((prev) => ({
      ...prev,
      search_keyword_quotas: [...(prev.search_keyword_quotas ?? []), emptyKeywordQuota()],
    }));
  };

  const updateKeywordQuota = (index: number, patch: Partial<DownloadKeywordQuotaDTO>) => {
    setAppInfo((prev) => ({
      ...prev,
      search_keyword_quotas: (prev.search_keyword_quotas ?? []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const removeKeywordQuota = (index: number) => {
    setAppInfo((prev) => ({
      ...prev,
      search_keyword_quotas: (prev.search_keyword_quotas ?? []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleLookup = async () => {
    const bundleId = appInfo.bundle_id.trim();
    const appId = appInfo.id;
    if (!bundleId && !appId) {
      addToast('error', '请先填写 App ID 或 Bundle ID');
      return;
    }
    setLookingUp(true);
    try {
      const result = await appStoreLookup({
        bundleId: appId ? undefined : bundleId || undefined,
        appId: appId || undefined,
        country: lookupCountry,
      });
      const currentQuotas = appInfo.search_keyword_quotas ?? [];
      const currentKeywords = currentQuotas.map((item) => item.keyword).filter(Boolean);
      const nextKeywords = currentKeywords.length > 0 ? currentKeywords : buildLookupSearchKeywords(result);
      const nextQuotas = currentQuotas.length > 0
        ? currentQuotas
        : buildSearchKeywordQuotasFromKeywords(nextKeywords);
      setAppInfo({ ...result, search_keywords: nextKeywords, search_keyword_quotas: nextQuotas });
      if (result.name) {
        setField('title', result.name);
      }
      if (result.artwork_url) {
        setField('icon_url', result.artwork_url);
      }
      addToast('success', nextKeywords.length > currentKeywords.length ? `应用信息查询成功，已生成 ${nextKeywords.length} 个搜索关键词` : '应用信息查询成功');
    } catch (e) {
      addToast('error', resolveLookupError(e));
    } finally {
      setLookingUp(false);
    }
  };

  const handleIconUpload = async (file: File | null) => {
    if (!file) return;
    try {
      setIconUploading(true);
      const result = await uploadImageToCos(file, 'task-icon');
      setUploadedIconUrl(result.readUrl);
      setField('icon_url', result.relativePath);
    } catch {
      addToast('error', '图标上传失败');
    } finally {
      setIconUploading(false);
    }
  };

  const isDownload = form.task_type === 'download';

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Link to="/tasks" className="hover:text-primary transition-colors">任务管理</Link>
        <span>/</span>
        <span className="text-gray-900">创建任务</span>
      </div>

      <p className="text-sm text-gray-500">填写以下信息创建新任务，创建后默认为草稿状态。</p>

      {/* 基础信息 */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-gray-900">基础信息</h2>

        <div className={basicInfoGridClass}>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">市场 <span className="text-red-500">*</span></label>
            <select value={form.market_id} onChange={(e) => setField('market_id', e.target.value)} className={selectCls}>
              <option value="">请选择市场</option>
              {marketConfigs.length === 0 ? (
                <option value="CN" disabled>CN · 中国</option>
              ) : marketConfigs.map((market) => (
                <option key={market.market_id} value={market.market_id}>{market.market_id} · {market.name}</option>
              ))}
            </select>
          </div>

          <div className="col-span-full flex items-center gap-2 whitespace-nowrap sm:col-span-2">
            <label className="shrink-0 text-sm font-medium text-gray-700">任务类型 <span className="text-red-500">*</span></label>
            <select value={form.task_type} onChange={(e) => setField('task_type', e.target.value)} className={`${selectCls} min-w-0 whitespace-nowrap`}>
              {enabledTaskTypes.map((t) => (
                <option key={t} value={t}>{TASK_TYPE_LABELS[t as AnyTaskType] ?? t}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">平台 <span className="text-red-500">*</span></label>
            <select
              value={isMessageSend ? 'ios' : form.platform}
              onChange={(e) => setField('platform', e.target.value)}
              disabled={isMessageSend}
              className={`${selectCls} disabled:bg-gray-50 disabled:text-gray-500`}
            >
              {ALL_TASK_PLATFORMS.map((p) => (
                <option key={p} value={p}>{TASK_PLATFORM_LABELS[p as TaskPlatform]}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">分类 <span className="text-red-500">*</span></label>
            <select value={form.category} onChange={(e) => setField('category', e.target.value)} className={selectCls}>
              {ALL_TASK_CATEGORIES.map((c) => (
                <option key={c} value={c}>{TASK_CATEGORY_LABELS[c as TaskCategory]}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">任务标题 <span className="text-red-500">*</span></label>
          <input type="text" value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="请输入任务标题" className={inputCls} />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">任务描述</label>
          <input type="text" value={form.description_title} onChange={(e) => setField('description_title', e.target.value)} placeholder={form.task_type === 'download' ? '默认: 任务要求' : '默认: 任务详情'} className={inputCls} />
          <RichTaskTextEditor value={form.description} onChange={(value) => setField('description', value)} placeholder="可选；不填写时客户端不展示该模块" />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">图标 URL</label>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px]">
            <input type="text" value={form.icon_url} onChange={(e) => setField('icon_url', e.target.value)} placeholder="https://... 或上传后自动生成" className={inputCls} />
            <label className="flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
              {iconUploading ? '上传中...' : '上传图标'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => void handleIconUpload(event.target.files?.[0] ?? null)}
              />
            </label>
          </div>
          {form.icon_url ? (
            <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              <img
                src={resolveImagePreviewUrl(form.icon_url, uploadedIconUrl ? { [form.icon_url]: uploadedIconUrl } : undefined)}
                alt="任务图标预览"
                className="h-24 w-24 object-cover"
              />
            </div>
          ) : null}
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">标签</label>
          <input type="text" value={form.tags} onChange={(e) => setField('tags', e.target.value)} placeholder="逗号分隔，如: 新手,限时,热门" className={inputCls} />
          <p className="text-xs text-gray-400">多个标签用英文逗号分隔</p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">任务规则</label>
          <input type="text" value={form.rules_title} onChange={(e) => setField('rules_title', e.target.value)} placeholder="默认: 任务规则" className={inputCls} />
          <RichTaskTextEditor value={form.rules} onChange={(value) => setField('rules', value)} placeholder="任务规则说明" />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">审核规则</label>
          <input type="text" value={form.review_rules_title} onChange={(e) => setField('review_rules_title', e.target.value)} placeholder="默认: 审核规则" className={inputCls} />
          <RichTaskTextEditor value={form.review_rules} onChange={(value) => setField('review_rules', value)} placeholder="审核判定规则" />
        </div>
      </section>

      {/* 奖励配置 */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-gray-900">奖励配置</h2>

        <div className={rewardGridClass}>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">奖励金额（{selectedMarket.currency_symbol} {selectedMarket.currency}）<span className="text-red-500">*</span></label>
            <input type="number" min="0" step={selectedMarket.currency_exponent > 0 ? '0.01' : '1'} value={form.rewardYuan} onChange={(e) => setField('rewardYuan', e.target.value)} placeholder={selectedMarket.currency_exponent > 0 ? '例如 12.50' : '例如 1000'} className={`${inputCls} tabular-nums`} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">最大名额</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.task_type === 'download' ? String(downloadMaxAssignments) : form.max_assignments}
              onChange={(e) => setField('max_assignments', e.target.value)}
              placeholder="0 = 不限"
              disabled={form.task_type === 'download' || isMessageSend}
              className={`${inputCls} tabular-nums disabled:bg-gray-50 disabled:text-gray-500`}
            />
            <p className="text-xs text-gray-400">{form.task_type === 'download' ? '下载任务由关键词名额自动累加' : isMessageSend ? '短信任务由号码池导入后自动同步' : '0 表示不限名额'}</p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">已完成数</label>
            <input type="number" min="0" step="1" value={form.completed_count} onChange={(e) => setField('completed_count', e.target.value)} className={`${inputCls} tabular-nums`} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">接取后提交时限（分钟）</label>
            <input type="number" min="0" step="0.5" value={form.assignment_timeout_minutes} onChange={(e) => setField('assignment_timeout_minutes', e.target.value)} className={`${inputCls} tabular-nums`} />
            <p className="text-xs text-gray-400">0 表示不启用超时释放。</p>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isMessageSend ? true : form.require_review}
              onChange={(e) => setField('require_review', e.target.checked)}
              disabled={isMessageSend}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-50"
            />
            需要人工审核
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.is_recommended} onChange={(e) => setField('is_recommended', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50" />
            推荐
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.is_new} onChange={(e) => setField('is_new', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50" />
            新任务标记
          </label>
        </div>
      </section>

      {/* 任务步骤 */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-gray-900">任务步骤</h2>
          <button onClick={addStep} className="text-sm text-primary hover:underline">+ 添加步骤</button>
        </div>

        {steps.length === 0 && <p className="text-sm text-gray-400">暂无步骤，点击右上方添加。</p>}

        {steps.map((step, idx) => (
          <TaskStepEditor
 allowVideo={!isMessageSend}
 onUploadingChange={busy=>setStepUploads(current=>({...current,[step.id]:busy}))}
            key={step.id}
            step={step}
            index={idx}
            total={steps.length}
            inputClassName={inputCls}
            onUpdate={(patch) => updateStep(idx, patch)}
            onMove={(dir) => moveStep(idx, dir)}
            onRemove={() => removeStep(idx)}
          />
        ))}
      </section>

      {/* 下载任务 App 信息 */}
      {form.task_type === 'normal' && (
        <section className="space-y-3">
          <h2 className="text-base font-medium text-gray-900">领取时访问应用（可选）</h2>
          <p className="text-sm text-gray-500">领取成功后，App 在后台访问一次指定应用页面。关键词不展示给用户；留空则不访问。</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-gray-700">领取时访问关键词
              <input value={appInfo.search_keywords?.[0] ?? ''} maxLength={100} onChange={(e) => setAppInfo((prev) => ({ ...prev, search_keywords: [e.target.value], search_keyword_quotas: [] }))} className={inputCls} placeholder="可选，例如：抖音" />
            </label>
            <label className="space-y-1 text-sm text-gray-700">App Store 应用 ID
              <input type="number" min="1" step="1" value={appInfo.id || ''} onChange={(e) => setAppInfo((prev) => ({ ...prev, id: Number(e.target.value) }))} className={inputCls} placeholder="例如：1142110895" />
            </label>
          </div>
        </section>
      )}

      {isDownload && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-medium text-gray-900">应用信息（下载任务）</h2>
            <div className="flex items-center gap-2">
              <select
                value={lookupCountry}
                onChange={(e) => setLookupCountry(e.target.value)}
                className={selectCls}
              >
                <option value="cn">中国大陆</option>
                <option value="us">美国</option>
              </select>
              <button
                onClick={handleLookup}
                disabled={lookingUp}
                className="text-sm text-primary hover:underline disabled:opacity-50 flex items-center gap-1.5"
              >
                {lookingUp && <span className="h-3 w-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />}
                从 App Store 查询
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">App ID</label>
              <input type="number" value={appInfo.id || ''} onChange={(e) => setAppField('id', parseInt(e.target.value, 10) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Bundle ID</label>
              <input type="text" value={appInfo.bundle_id} onChange={(e) => setAppField('bundle_id', e.target.value)} placeholder="com.example.app" className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">应用名称</label>
              <input type="text" value={appInfo.name} onChange={(e) => setAppField('name', e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">版本</label>
              <input type="text" value={appInfo.version} onChange={(e) => setAppField('version', e.target.value)} placeholder="1.0.0" className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">价格</label>
              <input type="number" min="0" step="0.01" value={appInfo.price || ''} onChange={(e) => setAppField('price', parseFloat(e.target.value) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">显示价格</label>
              <input type="text" value={appInfo.formatted_price} onChange={(e) => setAppField('formatted_price', e.target.value)} placeholder="¥0.00 / 免费" className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">开发者</label>
              <input type="text" value={appInfo.developer_name} onChange={(e) => setAppField('developer_name', e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">分类</label>
              <input type="text" value={appInfo.genre} onChange={(e) => setAppField('genre', e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">文件大小(bytes)</label>
              <input type="number" min="0" value={appInfo.file_size_bytes || ''} onChange={(e) => setAppField('file_size_bytes', parseInt(e.target.value, 10) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">评分</label>
              <input type="number" min="0" max="5" step="0.1" value={appInfo.rating || ''} onChange={(e) => setAppField('rating', parseFloat(e.target.value) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">评分数</label>
              <input type="number" min="0" value={appInfo.rating_count || ''} onChange={(e) => setAppField('rating_count', parseInt(e.target.value, 10) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">内容分级</label>
              <input type="text" value={appInfo.content_rating} onChange={(e) => setAppField('content_rating', e.target.value)} placeholder="4+" className={inputCls} />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">图标 URL</label>
            <input type="text" value={appInfo.artwork_url} onChange={(e) => setAppField('artwork_url', e.target.value)} placeholder="https://..." className={inputCls} />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">应用描述</label>
            <textarea value={appInfo.description} onChange={(e) => setAppField('description', e.target.value)} rows={3} className={textareaCls} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">搜索关键词配额</label>
              <button onClick={addKeywordQuota} className="text-sm text-primary hover:underline">+ 添加关键词</button>
            </div>
            <div className="space-y-2">
              {(appInfo.search_keyword_quotas ?? []).map((item, index) => (
                <div key={index} className="grid grid-cols-[minmax(0,1fr)_120px_80px] gap-2">
                  <input
                    type="text"
                    value={item.keyword}
                    onChange={(e) => updateKeywordQuota(index, { keyword: e.target.value })}
                    placeholder="关键词"
                    className={inputCls}
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.target_count || 1}
                    onChange={(e) => updateKeywordQuota(index, { target_count: parseInt(e.target.value, 10) || 1 })}
                    className={`${inputCls} tabular-nums`}
                  />
                  <button
                    onClick={() => removeKeywordQuota(index)}
                    className="h-9 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                  >
                    删除
                  </button>
                </div>
              ))}
            </div>
            {(appInfo.search_keyword_quotas ?? []).length === 0 && (
              <p className="text-sm text-gray-400">暂无关键词，点击右侧添加。</p>
            )}
            <p className="text-xs text-gray-400">按顺序分配关键词；当前词完成量达到目标量后，新领取用户自动切到下一个词。</p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">截图 URLs</label>
            <textarea
              value={(appInfo.screenshot_urls ?? []).join('\n')}
              onChange={(e) => setAppField('screenshot_urls', e.target.value.split('\n').filter(Boolean))}
              rows={3}
              placeholder="每行一个 URL"
              className={textareaCls}
            />
            <p className="text-xs text-gray-400">每行一个截图链接</p>
          </div>
        </section>
      )}

      {isMessageSend && (
        <MessageImportPanel
          mode="create"
          pendingFile={messageImportFile}
          onPendingFileChange={setMessageImportFile}
          idempotencyKey={messageImportKey}
          onIdempotencyKeyChange={setMessageImportKey}
        />
      )}

      {/* 时间与排序 */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-gray-900">时间与排序</h2>

        <div className={timeAndSortGridClass}>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">开始时间</label>
            <input type="datetime-local" value={form.start_at} onChange={(e) => setField('start_at', e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">结束时间</label>
            <input type="datetime-local" value={form.end_at} onChange={(e) => setField('end_at', e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="space-y-1 max-w-[200px]">
          <label className="text-sm font-medium text-gray-700">排序权重</label>
          <input type="number" min="0" step="1" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} className={`${inputCls} tabular-nums`} />
          <p className="text-xs text-gray-400">数值越大排序越靠前</p>
        </div>
      </section>

      {/* 操作栏 */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || Object.values(stepUploads).some(Boolean)}
          className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center gap-2"
        >
          {submitting && <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
          创建任务
        </button>
        <Link to="/tasks" className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
          取消
        </Link>
      </div>
    </div>
  );
}
